// InstallBanner.js — Floating toast, ONLY shows on mobile
// Desktop users don't need to install a PWA
import React, { useState, useEffect } from 'react';
import { Paper, Button, Typography, Box, IconButton } from '@mui/material';
import { GetApp, Close } from '@mui/icons-material';
import { useInstallPrompt } from '../hooks/useInstallPrompt';
import { isMobileDevice } from '../utils/mobileDetect';

const DISMISS_KEY = 'pwa_install_dismissed';
const DISMISS_DURATION = 14 * 24 * 60 * 60 * 1000; // 14 days

function wasRecentlyDismissed() {
  const dismissed = localStorage.getItem(DISMISS_KEY);
  if (!dismissed) return false;
  return Date.now() - parseInt(dismissed, 10) < DISMISS_DURATION;
}

export default function InstallBanner() {
  const { isInstallable, isInstalled, install } = useInstallPrompt();
  const [isMobile, setIsMobile] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setIsMobile(isMobileDevice());
    const handleResize = () => setIsMobile(isMobileDevice());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
    setDismissed(true);
  };

  // Only show on mobile, not installed, installable, not recently dismissed
  if (!isMobile || isInstalled || !isInstallable || wasRecentlyDismissed() || dismissed) {
    return null;
  }

  return (
    <Paper
      elevation={0}
      sx={{
        position: 'fixed',
        bottom: 16,
        left: 16,
        right: 16,
        zIndex: 1100,
        bgcolor: '#FFFFFF',
        border: '1px solid #E5E7EB',
        borderRadius: '12px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', p: 1.5, gap: 1.5 }}>
        {/* Refined app launcher icon */}
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '10px',
            bgcolor: '#1D4ED8',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            boxShadow: '0 2px 8px rgba(29,78,216,0.3)',
          }}
        >
          <Typography sx={{ color: 'white', fontSize: '1.1rem', fontWeight: 700 }}>C</Typography>
        </Box>

        {/* Text container */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            fontWeight={600}
            sx={{
              lineHeight: 1.3,
              fontSize: '0.875rem',
              color: '#0F172A',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            Install App
          </Typography>
          <Typography
            variant="caption"
            sx={{
              lineHeight: 1.3,
              color: '#64748B',
              fontSize: '0.75rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
            }}
          >
            Add to home screen for quick access
          </Typography>
        </Box>

        {/* Actions: Install button + Close */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<GetApp />}
            onClick={install}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '8px',
              height: 32,
              px: 2,
              fontSize: '0.75rem',
              bgcolor: '#1D4ED8',
              '&:hover': { bgcolor: '#1E40AF' },
            }}
          >
            Install
          </Button>
          <IconButton
            size="small"
            onClick={handleDismiss}
            sx={{
              p: 0.5,
              color: '#94A3B8',
              '&:hover': { color: '#475569' },
            }}
          >
            <Close sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>
    </Paper>
  );
}
